import { useCallback, useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { SegmentedBannerGrid } from '~/src/components/SegmentedBanner/SegmentedBannerGrid';
import { PaymentBlock } from '~/src/features/payments-abn/components/PaymentBlock';
import ResultPopup, { IPopupState } from '~/src/features/payments-abn/components/PopupResult';
import PaymentUnauthLayout from '~/src/features/payments-unauth-abn';
import { loadOffers } from '~/src/features/specialOffersContainer/store/actions/thunk';
import { selectNewOrViewedOffers } from '~/src/features/specialOffersContainer/store/selectors';
import { getABNVariant } from '~/src/helpers/abn-tests';
import { SHOW_SEASON_TICKET } from '~/src/helpers/city';
import { TState } from '~/src/store';
import { getClientPayment } from '~/src/store/actions/auth/getClientPayment';
import { openChat } from '~/src/store/actions/chat';
import { getLoyaltyAvailability } from '~/src/store/actions/loyalty';
import { fetchSeasonTickets } from '~/src/store/actions/wholesaleCheaper';

import AgreementInfo from './components/AgreementInfo';
import CardsList from './components/CardsList';
import MobileAppPopup from './components/MobileAppPopup';
import { mobileAppPopupAnalytics } from './components/MobileAppPopup/analytics';
import PaymentCheck from './components/PaymentCheck';
import PaymentType from './components/PaymentType';
import SeasonTicketPayment from './components/SeasonTicketPayment';
import SpecialOffer from './components/SpecialOffer';
import { pushDataLayer } from './helpers/pushDataLayer';
import { usePaymentResult } from './hooks/usePaymentResult';
import { EPaymentTypeIds, setPaymentMethod, setPaymentResult } from './store/actionCreators';
import { showSpecOffer } from './store/actions';
import { initialPaymentResult } from './store/reducers';

import * as ST from './styled';

const PaymentsLayout = (): JSX.Element => {
    const router = useRouter<string>();
    const dispatch = useDispatch();
    const {
        isAuth,
        payment: { paymentTypeId, result: { isOpenPopup, payStatus } },
        wholesaleCheaperData,
        stoppers: { stopper },
    } = useSelector((state: TState) => ({
        isAuth: state.auth.isAuth,
        payment: state.payment,
        wholesaleCheaperData: state.wholesaleCheaperData.wholesaleCheaperData,
        stoppers: selectNewOrViewedOffers(state),
    }), shallowEqual);

    const [isShowMobileAppPopup, setIsShowMobileAppPopup] = useState(false);

    const showSeasonTicketVar = getABNVariant(SHOW_SEASON_TICKET.testName);
    const isShowSeasonTicket = showSeasonTicketVar === SHOW_SEASON_TICKET.variants.SHOW;

    const popupData = usePaymentResult();

    /**
     * @param deliveryRequestId используется при покупке оборудования (доставка), для прокидывания идентификатора заказа в "платеж"
     * тк для доставки доступна только оплата картой, то за счет этого флага происходит скрытие выбора других способов оплаты, см <PaymentFrame />:119 line
     *
     * @param referrer используется для редиректа на необходимую страницу после успешной оплаты
     */
    const { s: queryPaySum = '', referrer = null } = router.query;

    useEffect(() => {
        if (isAuth && !wholesaleCheaperData) {
            dispatch(fetchSeasonTickets());
        }
    }, [isAuth, wholesaleCheaperData]);

    useEffect(() => {
        if (isAuth) {
            dispatch(getLoyaltyAvailability());
            dispatch(loadOffers(true));
        }
    }, [isAuth]);

    useEffect(() => {
        if (payStatus && payStatus > 0) {
            dispatch(getClientPayment());
        }
    }, [payStatus]);

    const preparePopupText = useCallback(() => {
        const { text, isNeedSupport, isBonusesPaymentSuccessfull } = popupData;

        return (!!text || isNeedSupport)
            ? <ST.PopupInfo>
                {isNeedSupport && !isBonusesPaymentSuccessfull
                    ? <>
                        {text}Попробуйте ещё раз или напишите в&nbsp;<p onClick={() => dispatch(openChat())}>поддержку</p>
                    </>
                    : text
                }
            </ST.PopupInfo>
            : <></>;
    }, [dispatch, popupData]);

    const currentPopupData = useMemo<IPopupState>(() => {
        const currentText = preparePopupText();

        return {
            ...popupData,
            isOpen: isOpenPopup,
            text: currentText,
        };
    }, [preparePopupText, popupData, isOpenPopup]);

    const onSuccessPayment = () => {
        if (stopper) {
            dispatch(showSpecOffer());
        } else {
            setIsShowMobileAppPopup(true);
            mobileAppPopupAnalytics.show();
        }
    };

    const onClosePopup = useCallback(() => {
        if (popupData.type === 'success') {
            onSuccessPayment();
        }

        dispatch(setPaymentResult(initialPaymentResult));
        dispatch(setPaymentMethod(null));
    }, [popupData.type, onSuccessPayment]);

    const onCloseMobileAppPopup = () => {
        setIsShowMobileAppPopup(false);

        const mainPageUrl = isAuth ? '/lk' : '/';
        router.push(referrer ? referrer : mainPageUrl);
    };

    const hasAvailableSeasonTicket = !!wholesaleCheaperData?.availableFlags.length;

    return (
        <ST.PageContainer>
            {isAuth
                ? <>
                    <ST.PageTitle>Оплата услуг</ST.PageTitle>
                    <ST.PageContent>
                        <ST.InfoBlock>
                            <AgreementInfo>
                                <Link href={'/lk/user/autopay'} passHref>
                                    <ST.SetupLink onClick={() => pushDataLayer('click on configure', 'click')}>Настроить</ST.SetupLink>
                                </Link>
                            </AgreementInfo>
                            <PaymentCheck/>
                            <CardsList/>
                        </ST.InfoBlock>
                        <ST.ActionsBlock>
                            {(hasAvailableSeasonTicket && isShowSeasonTicket)
                                ? <PaymentType/>
                                : <></>
                            }
                            {(paymentTypeId === EPaymentTypeIds.WHOLESALE && isShowSeasonTicket)
                                ? hasAvailableSeasonTicket ? <SeasonTicketPayment/> : null
                                : <>
                                    <PaymentBlock paymentSum={+queryPaySum}/>
                                    <ResultPopup {...currentPopupData} onClose={onClosePopup}/>
                                    <SpecialOffer/>
                                    <MobileAppPopup isOpen={isShowMobileAppPopup} onClose={onCloseMobileAppPopup}/>
                                </>
                            }
                        </ST.ActionsBlock>
                    </ST.PageContent>
                    <SegmentedBannerGrid alias={'lk_payments'} margin='64px 0'/>
                </>
                : <PaymentUnauthLayout/>
            }
        </ST.PageContainer>
    );
};

export default PaymentsLayout;
